import React, { useMemo } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  CardBody,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import type { RoleAction } from '../../../../services';

import { useInvertedLocalize } from '../../../../utils/localization';
import invertedMessages from '../messages';

import './RoleCardMetricsCompact.css';

// max number of app icons to show before showing overflow count
const MAX_APP_ICONS_TO_SHOW = 2;

interface RoleCardMetricsCompactProps {
  actions: RoleAction[]; // actions array containing endpoint/app information
  entitlementsCount: number;
  yearlyRequests: number;
}

/**
 * RoleCardMetricsCompact component displaying app icons and metrics
 * in a vertical list for the compact card variant
 *
 * @param props - The component props
 * @returns JSX element containing the compact metrics display
 */
function RoleCardMetricsCompact({
  actions,
  entitlementsCount,
  yearlyRequests,
}: RoleCardMetricsCompactProps) {
  const l = useInvertedLocalize(invertedMessages);

  // extract unique endpoints from actions array from API data
  const endpoints = useMemo(() => {
    const endpointsMap = new Map<string, { name: string; logo: string }>();

    actions.forEach((action) => {
      const { endpoint } = action.entitlementMetaData;
      const endpointName =
        endpoint.endpointDisplayName ?? endpoint.endpointName;

      // strip .svg extension if backend mistakenly sends it
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const cleanLogo = endpoint?.logo?.endsWith('.svg')
        ? // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          endpoint?.logo?.slice(0, -4)
        : endpoint.logo;

      endpointsMap.set(endpointName, {
        name: endpointName,
        logo: cleanLogo,
      });
    });

    return Array.from(endpointsMap.values());
  }, [actions]);

  // sort endpoints: non-null logos first, preserve original order otherwise
  const sortedEndpoints = useMemo(
    () =>
      [...endpoints].sort((a, b) => {
        if (a.logo && !b.logo) {
          return -1;
        }

        if (!a.logo && b.logo) {
          return 1;
        }

        return 0;
      }),
    [endpoints]
  );

  // compute app names string once and reuse
  const appNamesText = useMemo(
    () => sortedEndpoints.map((endpoint) => endpoint.name).join(', '),
    [sortedEndpoints]
  );

  // memoize tooltipConfigs to prevent infinite re-renders
  const appNamesTooltipConfigs = useMemo(
    () => ({
      text: appNamesText,
      placement: 'top' as const,
      containerClassName:
        'Roles-RoleCardMetricsCompact-appNamesTooltipContainer',
    }),
    [appNamesText]
  );

  return (
    <CardBody className='Roles-RoleCardMetricsCompact'>
      <Box className='Roles-RoleCardMetricsCompact-list'>
        <hr className='Roles-RoleCardMetricsCompact-separator' />

        {/* Apps row with icons and names */}
        <Box className='Roles-RoleCardMetricsCompact-row'>
          <InterPoint color='neutralBold' font='body3' />

          {/* layered app icons */}
          <Box className='Roles-RoleCardMetricsCompact-appIcons'>
            {(() => {
              // always show first MAX_APP_ICONS_TO_SHOW icons
              const visibleEndpoints = sortedEndpoints.slice(
                0,
                MAX_APP_ICONS_TO_SHOW
              );
              const remainingEndpointsCount =
                endpoints.length - MAX_APP_ICONS_TO_SHOW;
              const remainingEndpointsClasses = cx(
                'Roles-RoleCardMetricsCompact-appsRemainingCount',
                remainingEndpointsCount > 9 &&
                  'Roles-RoleCardMetricsCompact-appsRemainingCount--isDoubleDigit'
              );

              return (
                <>
                  {visibleEndpoints.map((endpoint) => (
                    <ApplicationIcon
                      key={endpoint.name}
                      logo={endpoint.logo}
                      size='xxSmall'
                      className='Roles-RoleCardMetricsCompact-appIcon'
                    />
                  ))}
                  {remainingEndpointsCount > 0 && (
                    <ApplicationIcon
                      number={`+${remainingEndpointsCount}`}
                      size='xxSmall'
                      classNames={{
                        presentationClassName: remainingEndpointsClasses,
                      }}
                    />
                  )}
                </>
              );
            })()}
          </Box>

          {/* app names */}
          <Typography
            kind='body2'
            lineLimit={1}
            tooltipConfigs={appNamesTooltipConfigs}
            className='Roles-RoleCardMetricsCompact-appNames'>
            {appNamesText}
          </Typography>
        </Box>

        {/* entitlements metric */}
        <Box className='Roles-RoleCardMetricsCompact-row'>
          <InterPoint color='neutralBold' font='body3' />
          <Icon
            kind='Entitlement'
            size='small'
            color='--color-foreground-neutral-bold'
          />
          <Typography kind='body2' color='--color-foreground-neutral-medium'>
            {l('{count, plural, one {# Entitlement} other {# Entitlements}}', {
              count: entitlementsCount,
            })}
          </Typography>
        </Box>

        {/* yearly requests metric */}
        <Box className='Roles-RoleCardMetricsCompact-row'>
          <InterPoint color='neutralBold' font='body3' />
          <Icon
            kind='RequestAccess'
            size='small'
            color='--color-foreground-neutral-bold'
          />
          <Typography kind='body2' color='--color-foreground-neutral-medium'>
            {l(
              '{count, plural, one {# Yearly Request} other {# Yearly Requests}}',
              { count: yearlyRequests }
            )}
          </Typography>
        </Box>
      </Box>
    </CardBody>
  );
}

export default RoleCardMetricsCompact;
